import { Fragment, useCallback } from 'react';

import { Menu as BaseMenu } from '@base-ui/react/menu';
import { useInheritedDarkMode } from 'components/Theme';
import { cn } from 'components/utils';

import {
  menuItemDescriptionRecipe,
  menuItemIconRecipe,
  menuItemLabelRecipe,
  menuItemRecipe,
  menuItemTextRecipe,
  menuPanelRecipe,
  menuPositionerRecipe,
  menuSeparatorRecipe,
} from './Menu.recipe';

import type { MenuProps } from './types';

/**
 * Design System 2.0 action menu — the "more actions" popover.
 *
 * Ported from the `Menu` component set (file 5aSkE0U4TeWbuIZDvccHjc, node 556:20624). Read the
 * `.ds-menu` block header in `design-system.css` before changing anything visual: that component is
 * a SELECTION list, so the separator and the destructive row here are extrapolated from the design
 * system's own precedents rather than authored, and are flagged for a designer.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer, and
 * because the panel is portalled that ancestor is re-resolved from the anchor on every open — see
 * `useInheritedDarkMode`.
 *
 * ── Anchored, not triggered ─────────────────────────────────────────────────────────────────────
 *
 * Base UI's `Menu` is ordinarily `Root > Trigger + Portal`, which would require every call site to
 * move its trigger inside the menu. `Menu.Positioner` takes an `anchor` element instead, so this
 * composition has NO `Menu.Trigger` and the trigger stays wherever the call site already renders it.
 * That is what lets the legacy `common/components/Menu` — a controlled `anchor` / `onClose` pair
 * whose trigger is a sibling — swap to this without a single call site changing.
 *
 * The trade is focus restoration: with no trigger, Base UI has nothing to return focus to on close.
 * Not a regression, because the legacy menu already sets `disableRestoreFocus`.
 *
 * Distinct from `Select`, which renders the same panel and rows but as a value picker: its rows have
 * a selected state and a trailing indicator, and this has neither, because an action menu's rows run
 * a command and close.
 */
export const Menu = ({
  open,
  anchor,
  onOpenChange,
  onOpenChangeComplete,
  options,
  side = 'bottom',
  align = 'center',
  className,
}: MenuProps) => {
  /**
   * The anchor doubles as the theme sentinel. It is a real node at the call site's position in the
   * tree, which is exactly what the hook needs to walk up from — so unlike `Dialog` and `Select`,
   * this component renders no hidden sentinel element of its own.
   */
  const { inheritedDarkClass } = useInheritedDarkMode(open, anchor);

  // Base UI hands out an `eventDetails` second argument that the legacy API has no use for.
  const handleOpenChange = useCallback((nextOpen: boolean) => onOpenChange(nextOpen), [onOpenChange]);

  return (
    <BaseMenu.Root open={open} onOpenChange={handleOpenChange} onOpenChangeComplete={onOpenChangeComplete}>
      {/* The theme class goes on the PORTAL, which is an ancestor of the panel — so the panel carries
          only its own `ds-menu` slot class and the ordinary `:where(.dark) .ds-menu` rule reaches it,
          exactly as for a menu that never portalled. */}
      <BaseMenu.Portal className={inheritedDarkClass}>
        <BaseMenu.Positioner
          anchor={anchor}
          side={side}
          align={align}
          // 8px of air between the trigger and the panel, matching the gap the design draws between
          // a menu and the control that opens it.
          sideOffset={8}
          className={menuPositionerRecipe()}
        >
          <BaseMenu.Popup className={cn(menuPanelRecipe(), className)} data-part="menu">
            {options.map(
              ({ id, label, description, icon, tone, disabled, onSelect, separatorBefore, ...rest }, index) => (
                <Fragment key={id}>
                  {/* Suppressed on the first row: a separator against the panel's own top padding
                      would read as a stray line rather than a group break. */}
                  {separatorBefore && index > 0 && <BaseMenu.Separator className={menuSeparatorRecipe()} />}

                  <BaseMenu.Item
                    className={menuItemRecipe({ tone })}
                    disabled={disabled}
                    data-testid={rest['data-testid']}
                    onClick={onSelect}
                  >
                    {icon && <span className={menuItemIconRecipe()}>{icon}</span>}
                    {/* The column is rendered whether or not there is a description, so a row's
                        label sits in the same box either way — see `menuItemTextRecipe`. Both lines
                        are inside the ITEM rather than beside it, which is what makes the
                        description part of the row's accessible name: a screen reader announces the
                        consequence along with the action. */}
                    <span className={menuItemTextRecipe()}>
                      <span className={menuItemLabelRecipe()}>{label}</span>
                      {description && (
                        <span className={menuItemDescriptionRecipe()} data-part="item-description">
                          {description}
                        </span>
                      )}
                    </span>
                  </BaseMenu.Item>
                </Fragment>
              )
            )}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
};
